import { useEffect, useMemo, useRef, useState } from 'react'

import { SkeletonRows } from '@/components/ui/skeleton'
import { DriversTable } from '../../../../components/DriversTable'
import { type Driver, useDrivers } from '../../../../models/driver'
import { useFeatureFlag } from '../../../../models/flag'
import { MVR_FEATURE_FLAG, useMvrEligibility } from '../../../../models/mvr'
import { UnreportedAccidentsBanner } from './UnreportedAccidentsBanner'

export function DriversTab({ orgId }: { orgId: string }) {
  const { drivers, isLoading } = useDrivers({ shouldFetchAllPages: true, orgId })
  const [selectedDrivers, setSelectedDrivers] = useState<Driver[]>([])
  const mvrEnabled = useFeatureFlag(MVR_FEATURE_FLAG)
  const { states, refetch: refetchMvrStates } = useMvrEligibility(orgId, mvrEnabled)

  useEffect(() => {
    setSelectedDrivers(drivers)
  }, [drivers])

  // The MVR warnings check each driver's reported accidents, but a driver edit refreshes only the drivers list.
  const driversLoaded = useRef(false)
  useEffect(() => {
    if (isLoading || !mvrEnabled) return
    if (driversLoaded.current) void refetchMvrStates()
    driversLoaded.current = true
  }, [drivers, isLoading, mvrEnabled, refetchMvrStates])

  // One fetch for the whole tab, keyed by driver, rather than a query per row.
  const mvrStates = useMemo(
    () => (mvrEnabled ? new Map(states.map((state) => [state.driverId, state])) : undefined),
    [mvrEnabled, states]
  )

  if (isLoading) {
    return <SkeletonRows />
  }

  return (
    <div className="flex flex-col gap-4">
      {mvrEnabled && <UnreportedAccidentsBanner states={states} />}
      <DriversTable
        drivers={selectedDrivers}
        setDrivers={setSelectedDrivers}
        addCtaOnTop
        searchable
        orgId={orgId}
        hideHeading
        mvrStates={mvrStates}
      />
    </div>
  )
}
